import React, { useState, useRef, useEffect } from "react";
import { api } from "../lib/api";
import Pip from "../components/Pip";
import WeaknessCard from "../components/WeaknessCard";
import MarkedText from "../components/MarkedText";
import { C } from "../lib/theme";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import HudFrame from "../components/HudFrame";
import { ArrowRight, Send, Loader2, AlertTriangle } from "lucide-react";

export default function Stage2({ session, onComplete }) {
  const [transcript, setTranscript] = useState(session.stage2.transcript || []);
  const [weaknesses, setWeaknesses] = useState(session.stage2.weaknesses || []);
  const [understanding, setUnderstanding] = useState(session.stage2.understanding || 0);
  const [mood, setMood] = useState("curious");
  const [msg, setMsg] = useState("");
  const [sending, setSending] = useState(false);
  const [complete, setComplete] = useState(false);
  const [rounds, setRounds] = useState((session.stage2.transcript || []).filter((m) => m.role === "user").length);

  const [fixW, setFixW] = useState(null);
  const [fixData, setFixData] = useState(null);
  const [fixMsg, setFixMsg] = useState("");
  const [fixBusy, setFixBusy] = useState(false);
  const [fixFollowup, setFixFollowup] = useState(null);

  const chatEnd = useRef(null);
  useEffect(() => { chatEnd.current?.scrollIntoView({ behavior: "smooth" }); }, [transcript]);

  // ---- curved connector lines from each ERROR card to its source message ----
  const wrapRef = useRef(null);
  const chatScrollRef = useRef(null);
  const cardRefs = useRef({});
  const msgRefs = useRef({});
  const [connectors, setConnectors] = useState([]);

  const drawConnectors = () => {
    if (!wrapRef.current || window.innerWidth < 1024) { setConnectors([]); return; }
    const cont = wrapRef.current.getBoundingClientRect();
    const chat = chatScrollRef.current ? chatScrollRef.current.getBoundingClientRect() : null;
    const lines = [];
    open.forEach((w) => {
      const cardEl = cardRefs.current[w.id];
      const msgEl = msgRefs.current[w.fromTurn];
      if (!cardEl || !msgEl) return;
      const cr = cardEl.getBoundingClientRect();
      const mr = msgEl.getBoundingClientRect();
      const side = leftIds.has(w.id) ? "left" : "right";
      const sx = (side === "left" ? cr.right : cr.left) - cont.left;
      const sy = cr.top + cr.height / 2 - cont.top;
      const ex = (side === "left" ? mr.left : mr.right) - cont.left;
      let ey = mr.top + mr.height / 2 - cont.top;
      let clipped = false;
      if (chat) {
        const top = chat.top - cont.top + 4;
        const bot = chat.bottom - cont.top - 4;
        if (ey < top) { ey = top; clipped = true; }
        if (ey > bot) { ey = bot; clipped = true; }
      }
      const dx = (ex - sx) * 0.45;
      const d = `M ${sx} ${sy} C ${sx + dx} ${sy}, ${ex - dx} ${ey}, ${ex} ${ey}`;
      lines.push({ id: w.id, d, sx, sy, ex, ey, active: w.id === activeFixId, clipped });
    });
    setConnectors(lines);
  };

  useEffect(() => {
    const raf = requestAnimationFrame(drawConnectors);
    const t = setTimeout(drawConnectors, 400);
    const onResize = () => drawConnectors();
    window.addEventListener("resize", onResize);
    const chatEl = chatScrollRef.current;
    if (chatEl) chatEl.addEventListener("scroll", drawConnectors);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(t);
      window.removeEventListener("resize", onResize);
      if (chatEl) chatEl.removeEventListener("scroll", drawConnectors);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transcript, weaknesses]);

  const open = weaknesses.filter((w) => w.status !== "fixed").sort((a, b) => (a.order || 0) - (b.order || 0));
  const fixed = weaknesses.filter((w) => w.status === "fixed");
  const activeFixId = open.length ? open[0].id : null;
  const half = Math.ceil(open.length / 2);
  const leftCards = open.slice(0, half);
  const rightCards = open.slice(half);
  const leftIds = new Set(leftCards.map((w) => w.id));

  const send = async () => {
    const text = msg.trim();
    if (!text || sending) return;
    setSending(true);
    setTranscript((t) => [...t, { role: "user", text }]);
    setMsg("");
    try {
      const r = await api.stage2Turn(session.id, text);
      setTranscript(r.transcript);
      setWeaknesses(r.weaknesses);
      setUnderstanding((prev) => Math.max(prev, r.understanding));
      setMood(r.mood);
      setComplete(r.complete);
      setRounds(r.turnCount);
    } catch (e) {
      setTranscript((t) => [...t, { role: "pip", text: "Oops, I got distracted! Can you say that again?", mood: "confused" }]);
    } finally {
      setSending(false);
    }
  };

  const openFix = async (w) => {
    if (w.id !== activeFixId) return;
    setFixW(w);
    setFixData(null);
    setFixFollowup(null);
    setFixMsg("");
    try {
      const d = await api.stage2Fix(session.id, w.id);
      setFixData(d);
    } catch (e) {
      setFixData({ miniLesson: "Couldn't load this fix. Close and try again.", reExplainPrompt: "" });
    }
  };

  const submitFix = async () => {
    if (!fixMsg.trim() || fixBusy) return;
    setFixBusy(true);
    try {
      const r = await api.stage2Verify(session.id, fixW.id, fixMsg.trim());
      setWeaknesses(r.weaknesses);
      setUnderstanding(r.understanding);
      setComplete(r.complete);
      if (r.fixed) {
        setMood("delighted");
        setFixW(null);
      } else {
        setFixFollowup(r.pipReply);
        setFixMsg("");
      }
    } finally {
      setFixBusy(false);
    }
  };

  const meterColor = understanding >= 80 ? C.grn : C.amb;

  return (
    <div className="mx-auto max-w-5xl" data-testid="stage2">
      <div className="mb-1 font-mono text-xs" style={{ color: C.amb }}>Stage 2 · Teach</div>
      <h2 className="font-display text-2xl sm:text-3xl">Explain it to Pip like they're five.</h2>
      <p className="mt-1 text-sm text-muted">Topic: {session.topic}</p>

      {/* understanding meter */}
      <div className="mt-5 rounded-lg border border-hair bg-surface p-4" data-testid="understanding-meter">
        <div className="mb-2 flex items-center justify-between">
          <span className="font-mono text-[11px] uppercase tracking-wider text-muted">Pip's understanding</span>
          <span className="font-mono text-lg" style={{ color: meterColor }}>{understanding}</span>
        </div>
        <div className="h-3 w-full overflow-hidden rounded-full bg-white/5">
          <div className="h-full rounded-full transition-all duration-700" style={{ width: `${understanding}%`, background: `linear-gradient(90deg, ${C.amb}, ${C.grn})` }} />
        </div>
      </div>

      {/* whiteboard */}
      <div ref={wrapRef} className="relative mt-6">
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_minmax(340px,1.6fr)_1fr]">
        {/* left cards */}
        <div className="order-2 space-y-4 lg:order-1">
          {leftCards.map((w) => (
            <div key={w.id} ref={(el) => { if (el) cardRefs.current[w.id] = el; }} className="relative lg:pr-4">
              <WeaknessCard weakness={w} onFix={openFix} side="left" number={w.order} locked={w.id !== activeFixId} />
            </div>
          ))}
        </div>

        {/* center: Pip + chat */}
        <HudFrame color="rgba(245,158,11,0.35)" className="order-1 lg:order-2">
        <div className="rounded-2xl border border-hair bg-surface p-5 hud-scan" data-testid="pip-whiteboard">
          <div className="flex justify-center"><Pip mood={mood} size={110} /></div>
          <div ref={chatScrollRef} className="mt-4 max-h-[42vh] space-y-3 overflow-y-auto pr-1">
            {transcript.length === 0 && (
              <p className="text-center text-sm text-muted">Pip is waiting. Start explaining in your own words — simple as you can.</p>
            )}
            {transcript.map((m, i) => {
              const userTurn = m.role === "user" ? transcript.slice(0, i + 1).filter((x) => x.role === "user").length : null;
              return (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  ref={userTurn != null ? (el) => { if (el) msgRefs.current[userTurn] = el; } : undefined}
                  className="max-w-[80%] rounded-2xl px-3 py-2 text-sm anim-fade-up"
                  style={m.role === "user"
                    ? { background: "rgba(255,255,255,0.06)", color: "#E6E8EE" }
                    : { background: `${C.amb}18`, color: "#E6E8EE", border: `1px solid ${C.amb}55` }}
                >
                  {m.role === "pip" && <span className="mr-1 font-mono text-[10px] uppercase" style={{ color: C.amb }}>Pip</span>}
                  {m.text}
                </div>
              </div>
              );
            })}
            {sending && <div className="flex justify-start"><div className="rounded-2xl px-3 py-2 text-sm text-muted"><Loader2 className="inline h-4 w-4 animate-spin" /> Pip is thinking…</div></div>}
            <div ref={chatEnd} />
          </div>

          <div className="mt-4 flex items-end gap-2">
            <textarea
              data-testid="stage2-input"
              value={msg}
              onChange={(e) => setMsg(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              rows={2}
              placeholder="Explain it simply…"
              className="flex-1 resize-none rounded-lg border border-hair bg-surface2 px-3 py-2 text-sm text-ink placeholder:text-muted focus:border-amb/60 focus:outline-none"
            />
            <button data-testid="stage2-send-btn" onClick={send} disabled={sending || !msg.trim()} className="press rounded-lg px-3 py-3 text-bg disabled:opacity-40" style={{ background: C.amb }}>
              <Send className="h-4 w-4" />
            </button>
          </div>
        </div>
        </HudFrame>

        {/* right cards */}
        <div className="order-3 space-y-4">
          {rightCards.map((w) => (
            <div key={w.id} ref={(el) => { if (el) cardRefs.current[w.id] = el; }} className="relative lg:pl-4">
              <WeaknessCard weakness={w} onFix={openFix} side="right" number={w.order} locked={w.id !== activeFixId} />
            </div>
          ))}
        </div>
      </div>

      {/* curved connectors overlay */}
      <svg className="pointer-events-none absolute inset-0 z-20 hidden h-full w-full lg:block" data-testid="weakness-connectors" aria-hidden="true">
        {connectors.map((c) => (
          <g key={c.id} style={{ opacity: c.active ? 0.95 : 0.5 }}>
            <path d={c.d} fill="none" stroke="#F43F5E" strokeWidth={c.active ? 1.8 : 1.2} strokeDasharray={c.active ? "none" : "3 3"} />
            <circle cx={c.sx} cy={c.sy} r="3" fill="#F43F5E" />
            <circle cx={c.ex} cy={c.ey} r={c.clipped ? 0 : 2.5} fill="#F43F5E" />
          </g>
        ))}
      </svg>
      </div>

      {/* fixed stack */}
      {fixed.length > 0 && (
        <div className="mt-6" data-testid="fixed-stack">
          <div className="mb-2 font-mono text-[11px] uppercase tracking-wider" style={{ color: C.grn }}>Fixed ({fixed.length})</div>
          <div className="flex flex-wrap gap-2">
            {fixed.map((w) => (
              <span key={w.id} className="rounded-full border px-3 py-1 text-xs" style={{ borderColor: C.grn, color: C.grn, background: `${C.grn}12` }}>
                {w.type}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* actions */}
      <div className="mt-8 flex flex-wrap items-center gap-4">
        {complete ? (
          <button data-testid="prove-it-btn" onClick={onComplete} className="flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold text-bg transition-transform hover:scale-[1.03] glow-green" style={{ background: C.grn }}>
            Prove it <ArrowRight className="h-4 w-4" />
          </button>
        ) : (
          <span className="text-sm text-muted">Get Pip to 80 with no open cards to unlock the test.</span>
        )}
        {rounds >= 3 && !complete && (
          <button data-testid="skip-to-test-link" onClick={onComplete} className="flex items-center gap-1.5 text-sm text-muted underline hover:text-ink">
            <AlertTriangle className="h-3.5 w-3.5 text-amb" /> Skip to the test — open gaps will probably show up
          </button>
        )}
      </div>

      {/* fix side sheet */}
      <Sheet open={!!fixW} onOpenChange={(o) => !o && setFixW(null)}>
        <SheetContent side="right" className="w-[90vw] max-w-md overflow-y-auto border-l border-hair bg-surface text-ink" data-testid="fix-sheet">
          <SheetHeader>
            <SheetTitle className="font-display text-xl text-ink">Learn it, then explain it</SheetTitle>
          </SheetHeader>
          {!fixData ? (
            <div className="mt-6 flex items-center gap-2 text-sm text-muted"><Loader2 className="h-4 w-4 animate-spin" /> Building a proper lesson for this gap…</div>
          ) : (
            <div className="mt-4">
              <div className="mono-label mb-1">THE CONCEPT</div>
              <div className="rounded-lg border border-hair bg-surface2 p-4">
                <MarkedText text={fixData.miniLesson} className="text-sm text-ink/90" />
              </div>
              {fixData.howToExplain && (
                <div className="mt-3 rounded-lg border p-4" style={{ borderColor: `${C.amb}55`, background: `${C.amb}10` }} data-testid="how-to-explain">
                  <div className="mono-label mb-1" style={{ color: C.amb }}>HOW TO EXPLAIN IT</div>
                  <p className="text-sm text-ink/90">{fixData.howToExplain}</p>
                </div>
              )}
              <p className="mt-4 text-sm text-ink">{fixFollowup || fixData.reExplainPrompt}</p>
              <textarea
                data-testid="fix-input"
                value={fixMsg}
                onChange={(e) => setFixMsg(e.target.value)}
                rows={3}
                placeholder="Re-explain just this part to Pip…"
                className="mt-2 w-full resize-none rounded-lg border border-hair bg-surface2 px-3 py-2 text-sm text-ink placeholder:text-muted focus:border-amb/60 focus:outline-none"
              />
              <button data-testid="fix-submit-btn" onClick={submitFix} disabled={fixBusy || !fixMsg.trim()} className="mt-3 flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-bg disabled:opacity-40" style={{ background: C.amb }}>
                {fixBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Tell Pip
              </button>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
